import { emit, listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

/** Propagate small device preferences to running webviews without a reload. */
export function syncPersistedPreferences<T extends object>({
  name,
  read,
  subscribe,
  apply,
}: {
  name: string;
  read: () => T;
  subscribe: (callback: () => void) => () => void;
  apply: (value: unknown) => void;
}): () => void {
  if (!isTauri()) return () => {};
  let applying = false;
  let disposed = false;
  let last = JSON.stringify(read());
  const event = `denker://preferences-${name}`;
  const stop = subscribe(() => {
    const value = read();
    const next = JSON.stringify(value);
    if (next === last) return;
    last = next;
    if (!applying) void emit(event, value).catch(() => {});
  });
  let unlisten: (() => void) | undefined;
  void listen<unknown>(event, ({ payload }) => {
    applying = true;
    try {
      apply(payload);
    } finally {
      applying = false;
    }
  })
    .then((cleanup) => {
      if (disposed) safeTauriUnlisten(cleanup);
      else unlisten = cleanup;
    })
    .catch(() => {});
  return () => {
    disposed = true;
    stop();
    if (unlisten) safeTauriUnlisten(unlisten);
  };
}
